import { createI18n, type CreateI18nOptions } from '@saniplus/api-client';
import type { Locale } from '@saniplus/contracts';
import type { i18n as I18nInstance } from 'i18next';

import frAuth from './fr/auth.json';
import frCommon from './fr/common.json';
import frHours from './fr/hours.json';
import frOffline from './fr/offline.json';
import frProfile from './fr/profile.json';
import frPunch from './fr/punch.json';
import frShifts from './fr/shifts.json';
import frTasks from './fr/tasks.json';

/**
 * D-13: catalogues are split per module (`src/i18n/{fr,en}/<module>.json`).
 * A feature adds its own namespace here; it never inlines a French string in
 * a component, and the ESLint `react/jsx-no-literals` rule on `pages/` and
 * `features/` is what stops it.
 *
 * Every namespace this app owns is registered here, in one place, so a
 * missing catalogue is a build error rather than a French key rendered raw on
 * a phone at 02:00. A new feature adds three lines: the French import, the
 * `resources` entry, and its name in `namespaces` — plus the English twin in
 * `./en-catalogues`.
 *
 * Only **French** is bundled. French is the product language, `index.html`
 * hardcodes `lang="fr-CA"`, and the English half is the same size again — so it
 * lives in `./en-catalogues` and is fetched the first time anybody asks for
 * English (see `initI18n`).
 */
export const resources: CreateI18nOptions['resources'] = {
  fr: {
    common: frCommon,
    offline: frOffline,
    auth: frAuth,
    profile: frProfile,
    punch: frPunch,
    shifts: frShifts,
    hours: frHours,
    tasks: frTasks,
  },
};

export const namespaces = [
  'common',
  'offline',
  'auth',
  'punch',
  'profile',
  'shifts',
  'hours',
  'tasks',
];

/**
 * Registers the English catalogues on an instance, fetching the chunk once.
 *
 * Exported because `initI18n` is synchronous and English is not: anything that
 * asserts an English string without going through `changeLanguage` — a spec
 * calling `getFixedT('en', ...)`, a render helper handed `locale: 'en'` — has
 * to await this first.
 *
 * The chunk itself is cached by the module system; the *registration* is not,
 * because every `initI18n` call is a fresh isolated instance (one per app, one
 * per test) and each needs its own bundles.
 */
let cataloguesPromise: Promise<Record<string, unknown>> | null = null;

export async function ensureEnglish(i18n: I18nInstance): Promise<void> {
  if (i18n.hasResourceBundle('en', 'common')) return;
  cataloguesPromise ??= import('./en-catalogues').then((m) => m.default);
  const catalogues = await cataloguesPromise;
  for (const [ns, bundle] of Object.entries(catalogues)) {
    i18n.addResourceBundle('en', ns, bundle, true, true);
  }
}

export function initI18n(defaultLocale: Locale) {
  const i18n = createI18n({ resources, defaultLocale, namespaces });

  /*
   * `changeLanguage` is wrapped rather than left to a `languageChanged`
   * listener so that the promise every caller already awaits — `useLocale`,
   * the specs — resolves with English *on screen*, not with English still in
   * flight. Nothing switches language until the catalogue is in the instance.
   *
   * Offline, the chunk is precached like every other `.js` the build emits, so
   * the switch works with no signal.
   */
  const changeLanguage = i18n.changeLanguage.bind(i18n);
  i18n.changeLanguage = async (lng, callback) => {
    if (lng !== undefined && lng.startsWith('en')) await ensureEnglish(i18n);
    return changeLanguage(lng, callback);
  };

  // A stored `en` preference: init already resolved to English, so the only
  // thing left is to fill it in and repaint. French shows until it lands.
  if (defaultLocale === 'en') {
    void ensureEnglish(i18n).then(() => i18n.emit('languageChanged', i18n.language));
  }

  return i18n;
}
